import type { ReactNode } from "react";
import type { AccountNavSectionId, NavSectionId } from "@/nav/navConfig";

const stroke = {
  fill: "none" as const,
  stroke: "currentColor",
  strokeWidth: 1.75,
  strokeLinecap: "round" as const,
  strokeLinejoin: "round" as const
};

function Svg({ children }: { children: ReactNode }) {
  return (
    <svg viewBox="0 0 24 24" fill="none" aria-hidden="true">
      {children}
    </svg>
  );
}

/** Professional outline icons for primary dashboard / account tabs. */
export function NavTabIcon({ id }: { id: NavSectionId | AccountNavSectionId }) {
  switch (id) {
    case "home":
      return (
        <Svg>
          <path d="M4 10.5 12 4l8 6.5V20a1 1 0 0 1-1 1h-5v-6H10v6H5a1 1 0 0 1-1-1v-9.5Z" {...stroke} />
        </Svg>
      );
    case "live-users":
      return (
        <Svg>
          <circle cx="9" cy="8" r="3" {...stroke} />
          <circle cx="17" cy="9" r="2.25" {...stroke} />
          <path d="M3.5 19c.8-3.2 2.9-5 5.5-5s4.7 1.8 5.5 5" {...stroke} />
          <path d="M14.2 14.2c1.5-.5 3.2-.2 4.8 1.3.6.6 1.1 1.4 1.5 2.5" {...stroke} />
        </Svg>
      );
    case "request-log":
      return (
        <Svg>
          <path d="M8 6h12M8 12h12M8 18h12" {...stroke} />
          <circle cx="4.5" cy="6" r="1.1" fill="currentColor" stroke="none" />
          <circle cx="4.5" cy="12" r="1.1" fill="currentColor" stroke="none" />
          <circle cx="4.5" cy="18" r="1.1" fill="currentColor" stroke="none" />
        </Svg>
      );
    case "recordings":
      return (
        <Svg>
          <rect x="3" y="5" width="18" height="14" rx="2.5" {...stroke} />
          <path d="M10 9.2v5.6L15.2 12 10 9.2Z" {...stroke} />
        </Svg>
      );
    case "leads":
      return (
        <Svg>
          <circle cx="12" cy="8" r="3.2" {...stroke} />
          <path d="M5 19.2c1-3.4 3.4-5.2 7-5.2s6 1.8 7 5.2" {...stroke} />
          <path d="M16.2 4.8 17.6 6.2 20.4 3.4" {...stroke} />
        </Svg>
      );
    case "pages":
      return (
        <Svg>
          <path d="M7 3.5h7.2L19 8.3V20a1 1 0 0 1-1 1H7a1 1 0 0 1-1-1V4.5a1 1 0 0 1 1-1Z" {...stroke} />
          <path d="M14 3.5V8h4.8M9 12h6M9 16h6" {...stroke} />
        </Svg>
      );
    case "experiments":
      return (
        <Svg>
          <path d="M9 3h6M10 3v5.2L5.8 17.2A2.4 2.4 0 0 0 8 20.5h8a2.4 2.4 0 0 0 2.2-3.3L13.9 8.2V3" {...stroke} />
          <path d="M8.2 14.5h7.6" {...stroke} />
        </Svg>
      );
    case "redirects":
      return (
        <Svg>
          <path d="M7 8h10l-3-3M17 8l-3 3" {...stroke} />
          <path d="M17 16H7l3 3M7 16l3-3" {...stroke} />
        </Svg>
      );
    case "deploy":
      return (
        <Svg>
          <path d="M12 3 4.5 19.5 12 15.8l7.5 3.7L12 3Z" {...stroke} />
          <path d="M12 15.8V21" {...stroke} />
        </Svg>
      );
    case "cache":
      return (
        <Svg>
          <ellipse cx="12" cy="6" rx="8" ry="3" {...stroke} />
          <path d="M4 6v5c0 1.7 3.6 3 8 3s8-1.3 8-3V6" {...stroke} />
          <path d="M4 11v5c0 1.7 3.6 3 8 3s8-1.3 8-3v-5" {...stroke} />
        </Svg>
      );
    case "firewall":
      return (
        <Svg>
          <path d="M12 3 5 6.2v5.3c0 4.6 3.1 7.8 7 9.5 3.9-1.7 7-4.9 7-9.5V6.2L12 3Z" {...stroke} />
          <path d="M9.2 12.1 11 13.9l3.8-3.8" {...stroke} />
        </Svg>
      );
    case "audit-log":
      return (
        <Svg>
          <path d="M9 4h6a2 2 0 0 1 2 2v14H7V6a2 2 0 0 1 2-2Z" {...stroke} />
          <path d="M9 4.5V3.8a1.3 1.3 0 0 1 1.3-1.3h3.4A1.3 1.3 0 0 1 15 3.8v.7" {...stroke} />
          <path d="M10 11h4M10 15h4" {...stroke} />
        </Svg>
      );
    case "compliance":
      return (
        <Svg>
          <path d="M12 3 5 6.2v5.3c0 4.6 3.1 7.8 7 9.5 3.9-1.7 7-4.9 7-9.5V6.2L12 3Z" {...stroke} />
          <path d="M9.2 12.1 11 13.9l3.8-3.8" {...stroke} />
        </Svg>
      );
    case "settings":
    case "global-settings":
      return (
        <Svg>
          <circle cx="12" cy="12" r="3" {...stroke} />
          <path
            d="M12 3.5v2.2M12 18.3v2.2M3.5 12h2.2M18.3 12h2.2M5.8 5.8l1.6 1.6M16.6 16.6l1.6 1.6M5.8 18.2l1.6-1.6M16.6 7.4l1.6-1.6"
            {...stroke}
          />
        </Svg>
      );
    case "sites":
      return (
        <Svg>
          <circle cx="12" cy="12" r="8.5" {...stroke} />
          <path d="M3.5 12h17M12 3.5c2.4 2.6 3.6 5.4 3.6 8.5S14.4 17.9 12 20.5C9.6 17.9 8.4 15.1 8.4 12S9.6 6.1 12 3.5Z" {...stroke} />
        </Svg>
      );
    case "profile":
      return (
        <Svg>
          <circle cx="12" cy="8" r="3.2" {...stroke} />
          <path d="M5 19.2c.9-3.4 3.4-5.2 7-5.2s6.1 1.8 7 5.2" {...stroke} />
        </Svg>
      );
    case "users":
      return (
        <Svg>
          <circle cx="9" cy="8" r="3" {...stroke} />
          <circle cx="17" cy="9" r="2.25" {...stroke} />
          <path d="M3.5 19c.8-3.2 2.9-5 5.5-5s4.7 1.8 5.5 5" {...stroke} />
          <path d="M14.2 14.2c1.5-.5 3.2-.2 4.8 1.3.6.6 1.1 1.4 1.5 2.5" {...stroke} />
        </Svg>
      );
    case "plans":
      return (
        <Svg>
          <rect x="3.5" y="5" width="17" height="14" rx="2" {...stroke} />
          <path d="M3.5 10h17M9 5v14" {...stroke} />
        </Svg>
      );
    case "billing":
      return (
        <Svg>
          <rect x="3.5" y="6" width="17" height="12" rx="2" {...stroke} />
          <path d="M3.5 10h17" {...stroke} />
          <path d="M8 15h3.5" {...stroke} />
        </Svg>
      );
    case "credit-rates":
      return (
        <Svg>
          <circle cx="12" cy="12" r="8.5" {...stroke} />
          <path d="M12 7.2v9.6M9.4 9.2c.6-1 1.5-1.5 2.6-1.5 1.6 0 2.7.9 2.7 2.2S13.6 12 12 12s-2.8.8-2.8 2.1c0 1.3 1.2 2.2 2.8 2.2 1.1 0 2-.5 2.6-1.5" {...stroke} />
        </Svg>
      );
    case "settings-skeletons":
      return (
        <Svg>
          <rect x="4" y="4" width="10" height="10" rx="1.5" {...stroke} />
          <rect x="10" y="10" width="10" height="10" rx="1.5" {...stroke} />
        </Svg>
      );
    default:
      return null;
  }
}
